import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { CreditCard, Plus, Trash2, CheckCircle, AlertTriangle, Clock, Receipt, Shield, Loader2, ExternalLink, Star, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientBilling({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [billing, setBilling] = useState(null);
  const [methods, setMethods] = useState([]);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingCard, setAddingCard] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);

  useEffect(() => {
    if (client?.slug) {
      fetchAll();
    }
  }, [client?.slug]);

  // Check for card_updated query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('card_updated') === 'true') {
      fetchAll();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchBilling(), fetchMethods()]);
    setLoading(false);
  };

  const fetchBilling = async () => {
    try {
      const res = await fetch(`${API}/api/client/payments/billing/${client.slug}`, { headers: getHeaders() });
      const data = await res.json();
      setBilling(data);
    } catch (err) { console.error(err); }
  };

  const fetchMethods = async () => {
    try {
      const res = await fetch(`${API}/api/client/payments/methods/${client.slug}`, { headers: getHeaders() });
      const data = await res.json();
      setMethods(data.methods || []);
      setDefaultMethod(data.default_method);
    } catch (err) { console.error(err); }
  };

  const addNewCard = async () => {
    setAddingCard(true);
    try {
      const res = await fetch(`${API}/api/client/payments/methods/${client.slug}/setup`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setAddingCard(false);
  };

  const setAsDefault = async (pmId) => {
    setSettingDefault(pmId);
    try {
      await fetch(`${API}/api/client/payments/methods/${client.slug}/default`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: pmId }),
      });
      setDefaultMethod(pmId);
      setMethods(prev => prev.map(m => ({ ...m, is_default: m.id === pmId })));
    } catch (err) { console.error(err); }
    setSettingDefault(null);
  };

  const removeCard = async (pmId) => {
    if (!window.confirm(t('billing.removeConfirm'))) return;
    try {
      await fetch(`${API}/api/client/payments/methods/${client.slug}/${pmId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      setMethods(prev => prev.filter(m => m.id !== pmId));
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  const sub = billing?.subscription;
  const statusColors = {
    active: 'text-green-400 bg-green-50',
    past_due: 'text-amber-400 bg-amber-900/30',
    suspended: 'text-red-400 bg-red-50',
    canceled: 'text-red-400 bg-red-50',
    pending_payment: 'text-yellow-400 bg-yellow-50',
  };

  const brandIcons = {
    visa: '💳 Visa',
    mastercard: '💳 Mastercard',
    amex: '💳 Amex',
    discover: '💳 Discover',
  };

  return (
    <div className="space-y-6" data-testid="client-billing">
      {/* Account Status Banner */}
      {billing?.account_status === 'suspended' && (
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 flex items-center gap-3" data-testid="suspension-banner">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-300 font-medium">{t('billing.suspendedTitle')}</p>
            <p className="text-red-400/70 text-sm">{t('billing.suspendedDesc')}</p>
          </div>
          <Button onClick={addNewCard} size="sm" className="bg-red-600 hover:bg-red-700">
            {t('billing.updatePayment')}
          </Button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Subscription Status */}
        <div className="bg-gray-800/50 border border-gray-200 rounded-xl p-5" data-testid="subscription-status">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-5 h-5 text-blue-400" />
            <h3 className="text-gray-900 font-semibold">{t('billing.subscription')}</h3>
          </div>

          {sub ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{t('billing.status')}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[sub.status] || statusColors.active}`}>
                  {sub.status === 'active' ? t('billing.active') : sub.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{t('billing.monthlyAmount')}</span>
                <span className="text-gray-900 font-medium">$199.00/mo</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{t('billing.currentPeriod')}</span>
                <span className="text-gray-900 text-sm">
                  {new Date(sub.current_period_start).toLocaleDateString()} — {new Date(sub.current_period_end).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{t('billing.nextBilling')}</span>
                <span className="text-gray-900 text-sm">{new Date(sub.current_period_end).toLocaleDateString()}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm mb-2">
                {billing?.setup_paid ? t('billing.noActiveSubscription') : t('billing.setupFeeFirst')}
              </p>
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="bg-gray-800/50 border border-gray-200 rounded-xl p-5" data-testid="payment-methods">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-400" />
              <h3 className="text-gray-900 font-semibold">{t('billing.paymentMethods')}</h3>
            </div>
            <Button onClick={addNewCard} disabled={addingCard} size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 text-xs" data-testid="add-card-btn">
              {addingCard ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
              {t('billing.addCard')}
            </Button>
          </div>

          {methods.length > 0 ? (
            <div className="space-y-2">
              {methods.map(m => (
                <div key={m.id} className="flex items-center justify-between bg-gray-200/30 rounded-lg p-3" data-testid={`card-${m.last4}`}>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-gray-900 text-sm font-medium">
                        {t('billing.endingIn', { brand: m.brand?.charAt(0).toUpperCase() + m.brand?.slice(1), last4: m.last4 })}
                      </p>
                      <p className="text-gray-400 text-xs">{t('billing.expires', { month: m.exp_month, year: m.exp_year })}</p>
                    </div>
                    {m.is_default && (
                      <span className="text-xs bg-blue-900/40 text-blue-400 px-2 py-0.5 rounded-full">{t('billing.default')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!m.is_default && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setAsDefault(m.id)}
                        disabled={settingDefault === m.id}
                        className="h-7 text-xs text-gray-500 hover:text-gray-900"
                      >
                        {settingDefault === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3 mr-1" />}
                        {t('billing.setDefault')}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeCard(m.id)} className="h-7 text-gray-400 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <CreditCard className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{t('billing.noMethods')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('billing.noMethodsHint')}</p>
            </div>
          )}
        </div>
      </div>

      {/* SMS Credits — Separate Billing */}
      <div className="bg-cyan-900/10 border border-cyan-800/30 rounded-xl p-5" data-testid="sms-credit-billing-notice">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-gray-900 font-semibold text-sm">{t('billing.smsSeparateTitle')}</h3>
            <p className="text-gray-500 text-xs mt-1 leading-relaxed">
              {t('billing.smsSeparateBody')}
            </p>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-gray-800/50 border border-gray-200 rounded-xl p-5" data-testid="billing-history">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-blue-400" />
          <h3 className="text-gray-900 font-semibold">{t('billing.billingHistory')}</h3>
        </div>

        {billing?.transactions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-gray-500 font-medium pb-2 pr-4">{t('billing.tableDate')}</th>
                  <th className="text-left text-gray-500 font-medium pb-2 pr-4">{t('billing.tableDescription')}</th>
                  <th className="text-right text-gray-500 font-medium pb-2 pr-4">{t('billing.tableAmount')}</th>
                  <th className="text-right text-gray-500 font-medium pb-2">{t('billing.tableStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {billing.transactions.map((tx, i) => (
                  <tr key={i} className="border-b border-gray-200">
                    <td className="py-2.5 pr-4 text-gray-600">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-900">{tx.package_name}</td>
                    <td className="py-2.5 pr-4 text-right text-gray-900 font-medium">${tx.total_amount?.toFixed(2)}</td>
                    <td className="py-2.5 text-right">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        tx.payment_status === 'paid' ? 'bg-green-50 text-green-400' :
                        tx.payment_status === 'pending' ? 'bg-yellow-50 text-yellow-400' :
                        'bg-red-50 text-red-400'
                      }`}>
                        {tx.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-4">{t('billing.noHistory')}</p>
        )}
      </div>

      {/* Security Note */}
      <div className="flex items-center gap-3 text-gray-400 text-xs">
        <Shield className="w-4 h-4 flex-shrink-0" />
        <p>{t('billing.secureFooter')}</p>
      </div>
    </div>
  );
}
